import React from 'react';
import { Server, Database, BarChart2, Network, Shield, Settings, Activity, AlertCircle } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';

export function SiemManagement() {
  const { language } = useLanguage();
  
  const content = {
    hero: {
      title: {
        fr: 'Gestion SIEM Avancée',
        en: 'Advanced SIEM Management'
      },
      subtitle: {
        fr: 'Solution complète de gestion des informations et événements de sécurité',
        en: 'Comprehensive security information and event management solution'
      }
    },
    features: [
      {
        icon: <Database className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Collecte Centralisée',
          en: 'Centralized Collection'
        },
        description: {
          fr: 'Agrégation de tous les logs et événements de sécurité',
          en: 'Aggregation of all security logs and events'
        }
      },
      {
        icon: <BarChart2 className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Analyse en Temps Réel',
          en: 'Real-time Analysis'
        },
        description: {
          fr: 'Traitement instantané des données de sécurité',
          en: 'Instant processing of security data'
        }
      },
      {
        icon: <Activity className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Détection Avancée',
          en: 'Advanced Detection'
        },
        description: {
          fr: 'Identification précise des menaces et anomalies',
          en: 'Precise identification of threats and anomalies'
        }
      }
    ],
    platform: {
      title: {
        fr: 'Notre Plateforme',
        en: 'Our Platform'
      },
      sections: [
        {
          icon: <Network className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Intégration Complète',
            en: 'Complete Integration'
          },
          description: {
            fr: 'Connexion avec tous vos systèmes et applications',
            en: 'Connection with all your systems and applications'
          },
          features: [
            {
              fr: 'Support multi-sources',
              en: 'Multi-source support'
            },
            {
              fr: 'Connecteurs personnalisés',
              en: 'Custom connectors'
            },
            {
              fr: 'Intégration cloud native',
              en: 'Native cloud integration'
            }
          ]
        },
        {
          icon: <Settings className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Configuration Flexible',
            en: 'Flexible Configuration'
          },
          description: {
            fr: 'Adaptation à vos besoins spécifiques',
            en: 'Adaptation to your specific needs'
          },
          features: [
            {
              fr: 'Règles personnalisables',
              en: 'Customizable rules'
            },
            {
              fr: 'Tableaux de bord modulaires',
              en: 'Modular dashboards'
            },
            {
              fr: 'Alertes configurables',
              en: 'Configurable alerts'
            }
          ]
        }
      ]
    },
    analytics: {
      title: {
        fr: 'Capacités Analytiques',
        en: 'Analytics Capabilities'
      },
      items: [
        {
          icon: <AlertCircle className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Machine Learning',
            en: 'Machine Learning'
          },
          description: {
            fr: 'Détection automatique des comportements anormaux',
            en: 'Automatic detection of abnormal behaviors'
          }
        },
        {
          icon: <Shield className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Corrélation Avancée',
            en: 'Advanced Correlation'
          },
          description: {
            fr: 'Analyse contextuelle des événements de sécurité',
            en: 'Contextual analysis of security events'
          }
        },
        {
          icon: <Activity className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Reporting Automatisé',
            en: 'Automated Reporting'
          },
          description: {
            fr: 'Génération automatique de rapports détaillés',
            en: 'Automatic generation of detailed reports'
          }
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      <div className="hero-pattern py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Server className="w-16 h-16 text-blue-500 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {content.hero.title[language]}
            </h1>
            <p className="text-xl text-gray-300">
              {content.hero.subtitle[language]}
            </p>
          </div>
        </div>
      </div>

      {/* SIEM Dashboard Preview */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80" 
              alt="Office 365 Events Dashboard"
              className="w-full h-auto"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'fr' ? 'Événements Office 365' : 'Office 365 Events'}
              </h3>
              <p className="text-gray-300">
                {language === 'fr' 
                  ? 'Surveillance complète des activités Office 365 avec analyse en temps réel.'
                  : 'Comprehensive Office 365 activity monitoring with real-time analysis.'}
              </p>
            </div>
          </div>
          <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1506318137071-a8e063b4bec0?auto=format&fit=crop&q=80" 
              alt="Firepower Statistics Dashboard"
              className="w-full h-auto"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'fr' ? 'Statistiques Firepower' : 'Firepower Statistics'}
              </h3>
              <p className="text-gray-300">
                {language === 'fr' 
                  ? 'Analyse détaillée des événements de sécurité réseau et des menaces détectées.'
                  : 'Detailed analysis of network security events and detected threats.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="container mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {content.features.map((feature, index) => (
            <div key={index} className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-lg border border-gray-700/50 hover:border-blue-500/50 transition-colors duration-300">
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-xl font-bold text-white mb-3">
                {feature.title[language]}
              </h3>
              <p className="text-gray-300">
                {feature.description[language]}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Platform Section */}
      <div className="bg-gray-800/50 py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            {content.platform.title[language]}
          </h2>
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            {content.platform.sections.map((section, index) => (
              <div key={index} className="bg-gray-900/50 p-8 rounded-lg">
                <div className="flex items-center mb-4">
                  {section.icon}
                  <h3 className="text-xl font-bold text-white ml-3">
                    {section.title[language]}
                  </h3>
                </div>
                <p className="text-gray-300 mb-6">
                  {section.description[language]}
                </p>
                <ul className="space-y-3">
                  {section.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-300">
                      <Shield className="w-4 h-4 text-blue-500 mr-2" />
                      {feature[language]}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Analytics Section */}
      <div className="container mx-auto px-6 py-20">
        <h2 className="text-3xl font-bold text-white text-center mb-12">
          {content.analytics.title[language]}
        </h2>
        <div className="max-w-4xl mx-auto space-y-8">
          {content.analytics.items.map((item, index) => (
            <div key={index} className="flex items-start space-x-4 bg-gray-800/50 p-6 rounded-lg">
              <div className="flex-shrink-0 bg-blue-500/10 p-3 rounded-lg">
                {item.icon}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {item.title[language]}
                </h3>
                <p className="text-gray-300">
                  {item.description[language]}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-6 py-20">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80')] opacity-10 bg-cover bg-center"></div>
          <div className="relative z-10">
            <h2 className="text-3xl font-bold text-white mb-4">
              {language === 'fr' ? 'Optimisez Votre Sécurité' : 'Optimize Your Security'}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {language === 'fr' 
                ? 'Découvrez la puissance de notre solution SIEM' 
                : 'Discover the power of our SIEM solution'}
            </p>
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
              {language === 'fr' ? 'Demander une Démo' : 'Request a Demo'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}